import { useCallback, useMemo, useState } from 'react';
import { ArrowLeft } from 'react-feather';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import type { MessageTestingCell } from '@trinity/chat/types/message-testing';
import { ScoreBadge, CellDetailContent, COLORS } from './ScoreBadge';
import type { SegmentOption, TestableMessage } from './types';

// ─── Drill-in state ─────────────────────────────────────────────────────────

interface DrillInState {
  cell: MessageTestingCell;
  messageName: string;
  segmentName: string;
}

// ─── Pivot Table ────────────────────────────────────────────────────────────

interface MessageTestingPivotTableProps {
  messages: TestableMessage[];
  segments: SegmentOption[];
  cells: MessageTestingCell[];
  cellErrors: Map<string, string>;
}

export function MessageTestingPivotTable({
  messages,
  segments,
  cells,
  cellErrors,
}: MessageTestingPivotTableProps): React.JSX.Element {
  const [drillIn, setDrillIn] = useState<DrillInState | null>(null);

  const clearDrillIn = useCallback(() => setDrillIn(null), []);

  const cellMap = useMemo(() => {
    const map = new Map<string, Map<string, MessageTestingCell>>();
    for (const cell of cells) {
      if (!map.has(cell.messageId)) map.set(cell.messageId, new Map());
      map.get(cell.messageId).set(cell.segmentId, cell);
    }
    return map;
  }, [cells]);

  const showCodeColumn = useMemo(() => messages.some((m) => (m.code || '').trim().length > 0), [messages]);

  if (drillIn) {
    return (
      <DrillInRoot>
        <BackLink onClick={clearDrillIn}>
          <ArrowLeft size={14} />
          Back to Matrix
        </BackLink>
        <DrillInHeader>
          <DrillInRow>
            <DrillInLabel>Message</DrillInLabel>
            <DrillInValue>{drillIn.messageName}</DrillInValue>
          </DrillInRow>
          <DrillInRow>
            <DrillInLabel>Persona</DrillInLabel>
            <DrillInValue>{drillIn.segmentName}</DrillInValue>
          </DrillInRow>
        </DrillInHeader>
        <DrillInBody>
          <CellDetailContent cell={drillIn.cell} />
        </DrillInBody>
      </DrillInRoot>
    );
  }

  return (
    <GridScroller>
      <PivotTable>
        <thead>
          <tr>
            {showCodeColumn && <CodeHeaderCell>Code</CodeHeaderCell>}
            <MessageHeaderCell>Messages</MessageHeaderCell>
            {segments.map((seg) => (
              <SegmentHeaderCell key={seg.identifier}>{seg.name}</SegmentHeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {messages.map((msg) => {
            const msgId = msg.id;
            return (
              <tr key={msgId}>
                {showCodeColumn && <CodeCell>{msg.code}</CodeCell>}
                <MessageNameCell>
                  <Tooltip title={msg.body} placement="right" enterDelay={300}>
                    <MessageName>{msg.displayTitle}</MessageName>
                  </Tooltip>
                </MessageNameCell>
                {segments.map((seg) => {
                  const cell = cellMap.get(msgId)?.get(seg.identifier);
                  const cellError = cellErrors.get(`${msgId}:${seg.identifier}`);
                  let cellContent: React.ReactNode;
                  if (cell) {
                    cellContent = (
                      <ScoreBadge
                        cell={cell}
                        onDrillIn={() =>
                          setDrillIn({ cell, messageName: msg.displayTitle, segmentName: seg.name })
                        }
                      />
                    );
                  } else if (cellError) {
                    cellContent = <ErrorCell>Error</ErrorCell>;
                  } else {
                    cellContent = <PendingCell>&mdash;</PendingCell>;
                  }
                  return (
                    <td key={seg.identifier}>
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </PivotTable>
    </GridScroller>
  );
}

// ─── Styled ─────────────────────────────────────────────────────────────────

const GridScroller = styled.div`
  flex: 1;
  overflow: auto;
`;

const PivotTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  th,
  td {
    padding: 0;
  }

  thead tr {
    background: #f8f9fa;
    border-bottom: 2px solid #e0e0e0;
  }

  tbody td {
    border-bottom: 1px solid #e8e8e8;
  }

  tbody tr:hover {
    background: #f9f9f9;
  }
`;

const MessageHeaderCell = styled.th`
  text-align: left;
  padding: 0 8px;
  height: 36px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #1a1a1a;
  min-width: 180px;
  position: sticky;
  left: 0;
  background: #f8f9fa;
  z-index: 2;
`;

const CodeHeaderCell = styled.th`
  text-align: center;
  padding: 0 8px;
  height: 36px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #1a1a1a;
  min-width: 60px;
  white-space: nowrap;
`;

const CodeCell = styled.td`
  text-align: center;
  padding: 10px 8px;
  font-size: 11px;
  font-family: var(--font-semibold);
  color: #555;
  white-space: nowrap;
`;

const SegmentHeaderCell = styled.th`
  text-align: center;
  padding: 0 8px;
  height: 36px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #1a1a1a;
  min-width: 120px;
`;

const MessageNameCell = styled.td`
  padding: 10px 8px;
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
  min-width: 180px;
`;

const MessageName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #1a1a1a;
  cursor: default;
  display: block;
  word-break: break-word;
  white-space: normal;
  line-height: 1.4;
`;

const PendingCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  color: #bbb;
  font-size: 14px;
`;

const ErrorCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 8px 6px;
  margin: 4px;
  border-radius: 4px;
  background: ${COLORS.red.bg};
  border: 1px solid ${COLORS.red.border};
  color: ${COLORS.red.text};
  font-size: 11px;
  font-weight: 600;
  cursor: default;
`;

// ─── Drill-in styled components ─────────────────────────────────────────────

const DrillInRoot = styled.div`
  flex: 1;
  overflow: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 800px;
  margin: 0 auto;
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: var(--hyperlink, #6366f1);
  padding: 0;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

const DrillInHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const DrillInRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const DrillInLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #999;
  min-width: 56px;
`;

const DrillInValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
`;

const DrillInBody = styled.div`
  flex: 1;
`;
